<script lang="ts">
    import Chart from 'chart.js/auto'
    import type { Plugin } from 'chart.js'
    import ChartDataLabels from 'chartjs-plugin-datalabels'
    import dayjs from 'dayjs'
    import { onMount } from 'svelte/internal'
    import type { Charge } from '../../modules/paymentsApi/charges/charge.entity'
    import { ChargeStatus, ChargeStatusFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import type { Purchase } from '../../modules/paymentsApi/purchases/purchase.entity'
    import { PurchaseStatus, PurchaseStatusFriendly, PurchaseType } from '../../modules/paymentsApi/purchases/purchase.enum'
    import DateSelect from '../_common/date-select.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zinput from '../_common/z/zinput.svelte'

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()

    let containerEvolucao: HTMLCanvasElement
    let containerStatus: HTMLCanvasElement
    let containerAprovacao: HTMLCanvasElement
    let containerMetodo: HTMLCanvasElement
    let containerAssinaturas: HTMLCanvasElement
    let containerAtraso: HTMLCanvasElement
    let containerInadCanc: HTMLCanvasElement
    let containerCrescimento: HTMLCanvasElement
    let containerTempoCancelamento: HTMLCanvasElement
    let containerTipo: HTMLCanvasElement
    let containerTicketTipo: HTMLCanvasElement
    let containerTicketTipoPeriodo: HTMLCanvasElement
    let show

    let charts: Chart[] = []

    // safra = data de criação da assinatura. Filtra só os "Indicadores" — menos o crescimento líquido, que já é
    // uma linha do tempo mês a mês — e não refaz a busca: a base inteira já vem carregada em purchasesBase.
    let safra = 'all'
    let safraOptions: { value: string; label: string }[] = [{ value: 'all', label: 'Desde o início' }]
    let purchasesBase: Purchase[] = []
    const SAFRA_WINDOWS = [6, 12, 24]

    const MUTED = '#666'
    const GRID = '#555'

    // cores por semântica de status (sucesso / atenção / falha), não por categoria arbitrária
    const STATUS_COLOR: Record<string, string> = {
        paid: '#379E75',
        paid_manual: '#379E75',
        pending: '#FEB019',
        processing: '#FEB019',
        scheduled: '#FEB019',
        pre_authorized: '#008FFB',
        declined: '#D0483E',
        failed: '#D0483E',
        reversed: '#D0483E',
        charged_back: '#D0483E',
        dispute: '#D0483E',
        refunded: '#775DD0',
        canceled: '#9AA0A6',
        canceled_manual: '#9AA0A6',
        integration_error: '#9AA0A6',
        unknown: '#9AA0A6',
        aborted: '#9AA0A6',
    }

    const PURCHASE_STATUS_COLOR: Record<string, string> = {
        active: '#379E75',
        pending: '#FEB019',
        overdue: '#D0483E',
        canceled: '#9AA0A6',
    }

    const PAYMENT_METHOD_LABEL: Record<string, string> = {
        credit: 'Cartão de crédito',
        pix: 'Pix',
        bankslip: 'Boleto',
    }
    const PAYMENT_METHOD_COLOR: Record<string, string> = {
        credit: '#008FFB',
        pix: '#379E75',
        bankslip: '#FEB019',
    }

    const PURCHASE_TYPE_LABEL: Record<string, string> = {
        recurring_fixed: 'Recorrente (prazo fixo)',
        recurring_continuous: 'Recorrente (contínua)',
        full_charge: 'Cobrança única',
    }
    const PURCHASE_TYPE_COLOR: Record<string, string> = {
        recurring_fixed: '#008FFB',
        recurring_continuous: '#379E75',
        full_charge: '#FEB019',
    }

    function dateChanged(event) {
        dates = event.detail
        getData()
    }

    async function getData() {
        try {
            show = true

            // "Indicadores" busca a base inteira de assinaturas e recorta pela safra aqui no front;
            // "Por período" usa só o que foi criado/vencido dentro do intervalo selecionado. Os dois em paralelo.
            let [chargesRaw, purchasesAll, purchasesPeriodo]: [Charge[], Purchase[], Purchase[]] = await Promise.all([
                window.api.payments.charges.getAll({
                    from: dates.from,
                    to: dates.to,
                    purchase: true,
                    dateBy: 'dueDate',
                }),
                window.api.payments.purchases.getAll({ charges: false }),
                window.api.payments.purchases.getAll({ from: dates.from, to: dates.to, charges: false }),
            ])
            purchasesPeriodo = purchasesPeriodo.filter((el) => el.status !== PurchaseStatus.denied)
            purchasesAll = purchasesAll.filter((el) => el.status !== PurchaseStatus.denied)

            // "negado" e "anulado" são ignorados em todos os relatórios de parcelas
            const charges = chargesRaw.filter((c) => c.status !== ChargeStatus.declined && c.status !== ChargeStatus.aborted)
            console.log('data', charges, purchasesAll, purchasesPeriodo)

            charts.forEach((c) => c.destroy())
            charts = []

            generateEvolucao(charges)
            generateStatusShare(charges)
            generateApprovalRate(charges)
            generatePaymentMethod(charges)

            purchasesBase = purchasesAll
            buildSafraOptions(purchasesBase)
            // linha do tempo dos últimos 24 meses: sempre sobre a base toda, a safra não se aplica
            generateCrescimento(recurringOf(purchasesBase))
            renderIndicadores()

            generateTicketPorTipo(purchasesPeriodo, containerTicketTipoPeriodo)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function recurringOf(purchases: Purchase[]) {
        return purchases.filter((p) => p.purchaseType === PurchaseType.recurring_continuous || p.purchaseType === PurchaseType.recurring_fixed)
    }

    // janelas recentes primeiro, depois os anos que existem na base (mais recente primeiro)
    function buildSafraOptions(purchases: Purchase[]) {
        const years = [...new Set(purchases.map((p) => new Date(p.createdAt).getFullYear()))].sort((a, b) => b - a)
        safraOptions = [
            { value: 'all', label: 'Desde o início' },
            ...SAFRA_WINDOWS.map((m) => ({ value: 'm' + m, label: 'Últimos ' + m + ' meses' })),
            ...years.map((y) => ({ value: String(y), label: String(y) })),
        ]
        if (!safraOptions.some((o) => o.value === safra)) safra = 'all'
    }

    function inSafra(purchase: Purchase) {
        if (safra === 'all') return true
        const created = new Date(purchase.createdAt)
        if (safra.startsWith('m')) return created >= dayjs().subtract(Number(safra.substring(1)), 'month').toDate()
        return created.getFullYear() === Number(safra)
    }

    function destroyCharts(canvases: HTMLCanvasElement[]) {
        const doomed = charts.filter((c) => canvases.includes(c.canvas))
        doomed.forEach((c) => c.destroy())
        charts = charts.filter((c) => !doomed.includes(c))
    }

    // redesenha só os indicadores recortados pela safra (chamado no carregamento e a cada troca de safra)
    function renderIndicadores() {
        destroyCharts([containerAssinaturas, containerInadCanc, containerAtraso, containerTempoCancelamento, containerTipo, containerTicketTipo])
        const safraPurchases = purchasesBase.filter(inSafra)
        const recurring = recurringOf(safraPurchases)
        generateSubscriptionStatus(recurring)
        generateInadimplenciaCancelamento(recurring)
        generateOverdueHistogram(recurring)
        generateTempoCancelamento(recurring)
        generateTipoDistribuicao(safraPurchases, containerTipo)
        generateTicketPorTipo(safraPurchases, containerTicketTipo)
    }

    function currency(val: number) {
        return window.configs.currency + Number(val || 0).toFixed(2)
    }

    function hexToRgba(hex: string, alpha: number) {
        const n = parseInt(hex.replace('#', ''), 16)
        return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
    }

    // agrupa o período em dias (ou semanas, se o período for longo)
    function buildBuckets(from: Date, to: Date) {
        const totalDays = Math.ceil((to.getTime() - from.getTime()) / 86400000) + 1
        const stepDays = totalDays > 62 ? 7 : 1
        let buckets: { from: Date; to: Date; label: string }[] = []
        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + stepDays)) {
            const bucketFrom = new Date(d)
            const bucketTo = new Date(d)
            bucketTo.setDate(bucketTo.getDate() + stepDays - 1)
            bucketTo.setHours(23, 59, 59, 999)
            buckets.push({
                from: bucketFrom,
                to: bucketTo,
                label: stepDays === 1 ? dayjs(bucketFrom).format('DD/MM') : 'sem ' + dayjs(bucketFrom).format('DD/MM'),
            })
        }
        return buckets
    }

    // últimos `count` meses (calendário fechado), do mais antigo para o mais recente, terminando no mês atual
    function buildMonthBuckets(count: number) {
        const base = dayjs().startOf('month')
        let buckets: { from: Date; to: Date; label: string }[] = []
        for (let i = count - 1; i >= 0; i--) {
            const monthStart = base.subtract(i, 'month')
            buckets.push({
                from: monthStart.toDate(),
                to: monthStart.endOf('month').toDate(),
                label: monthStart.format('MM/YYYY'),
            })
        }
        return buckets
    }

    async function generateEvolucao(charges: Charge[]) {
        const buckets = buildBuckets(dates.from, dates.to)
        const cobrado = buckets.map((b) =>
            Number(
                charges
                    .filter((c) => c.dueDate && new Date(c.dueDate) >= b.from && new Date(c.dueDate) <= b.to)
                    .reduce((total, c) => total + c.value, 0)
                    .toFixed(2),
            ),
        )
        const recebido = buckets.map((b) =>
            Number(
                charges
                    .filter(
                        (c) =>
                            (c.status === ChargeStatus.paid || c.status === ChargeStatus.paid_manual) &&
                            c.paidAt &&
                            new Date(c.paidAt) >= b.from &&
                            new Date(c.paidAt) <= b.to,
                    )
                    .reduce((total, c) => total + c.value, 0)
                    .toFixed(2),
            ),
        )

        const chart = new Chart(containerEvolucao, {
            type: 'line',
            data: {
                labels: buckets.map((b) => b.label),
                datasets: [
                    {
                        label: 'Cobrado (vencimento)',
                        data: cobrado,
                        borderColor: '#008FFB',
                        backgroundColor: hexToRgba('#008FFB', 0.35),
                        fill: true,
                        tension: 0.3,
                        borderWidth: 2,
                        pointRadius: 0,
                    },
                    {
                        label: 'Recebido (pagamento)',
                        data: recebido,
                        borderColor: '#379E75',
                        backgroundColor: hexToRgba('#379E75', 0.35),
                        fill: true,
                        tension: 0.3,
                        borderWidth: 2,
                        pointRadius: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.dataset.label + ': ' + currency(ctx.parsed.y) } },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
        })
        charts.push(chart)
    }

    async function generateStatusShare(charges: Charge[]) {
        let counts: { label: string; qtde: number; color: string }[] = []
        for (const charge of charges) {
            const key = charge.statusLabel
            let item = counts.find((el) => el.label === key)
            if (!item) {
                item = { label: key, qtde: 0, color: STATUS_COLOR[key] || '#9AA0A6' }
                counts.push(item)
            }
            item.qtde += 1
        }
        counts.sort((a, b) => b.qtde - a.qtde)

        const chart = new Chart(containerStatus, {
            type: 'doughnut',
            data: {
                labels: counts.map((c) => ChargeStatusFriendly[c.label] || c.label),
                datasets: [{ data: counts.map((c) => c.qtde), backgroundColor: counts.map((c) => c.color), borderColor: 'transparent', borderWidth: 2 }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.label + ': ' + ctx.parsed } },
                    datalabels: { color: '#fff', font: { size: 11, weight: 'bold' }, formatter: (val, ctx) => (val > 0 ? val : '') },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateApprovalRate(charges: Charge[]) {
        const success = charges.filter((c) => c.status === ChargeStatus.paid || c.status === ChargeStatus.paid_manual).length
        const failed = charges.filter((c) =>
            // 'expired' entra aqui porque é o documento que venceu sem pagamento — era o que o
            // boleto vencido virava antes, como 'canceled'. Sem ele, a taxa de aprovação subiria
            // sozinha ao simplesmente ignorar as cobranças que ninguém pagou.
            [
                ChargeStatus.failed,
                ChargeStatus.reversed,
                ChargeStatus.canceled,
                ChargeStatus.canceled_manual,
                ChargeStatus.charged_back,
                ChargeStatus.expired,
            ].includes(c.status),
        ).length
        const concluded = success + failed
        const percent = concluded > 0 ? (success / concluded) * 100 : 0
        const color = percent >= 90 ? '#379E75' : percent >= 70 ? '#FEB019' : '#D0483E'

        const centerText: Plugin<'doughnut'> = {
            id: 'centerTextAprovacao',
            afterDraw(chart) {
                const { ctx, chartArea } = chart
                const x = (chartArea.left + chartArea.right) / 2
                const y = (chartArea.top + chartArea.bottom) / 2
                ctx.save()
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.font = 'bold 28px sans-serif'
                ctx.fillStyle = color
                ctx.fillText(percent.toFixed(0) + '%', x, y - 8)
                ctx.font = '13px sans-serif'
                ctx.fillStyle = MUTED
                ctx.fillText('Taxa de aprovação', x, y + 18)
                ctx.restore()
            },
        }

        const chart = new Chart(containerAprovacao, {
            type: 'doughnut',
            data: {
                labels: ['Aprovadas', 'Não aprovadas'],
                datasets: [{ data: [percent, 100 - percent], backgroundColor: [color, hexToRgba('#999999', 0.2)], borderWidth: 0 }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '75%',
                plugins: { legend: { display: false }, tooltip: { enabled: false } },
            },
            plugins: [centerText],
        })
        charts.push(chart)
    }

    async function generatePaymentMethod(charges: Charge[]) {
        let counts: { key: string; qtde: number }[] = []
        for (const charge of charges) {
            const key = charge.paymentMethod || 'other'
            let item = counts.find((el) => el.key === key)
            if (!item) {
                item = { key, qtde: 0 }
                counts.push(item)
            }
            item.qtde += 1
        }
        counts = counts.filter((c) => c.key !== 'other' || c.qtde > 0)
        counts.sort((a, b) => b.qtde - a.qtde)

        const chart = new Chart(containerMetodo, {
            type: 'bar',
            data: {
                labels: counts.map((c) => PAYMENT_METHOD_LABEL[c.key] || 'Outro'),
                datasets: [
                    {
                        label: 'Cobranças',
                        data: counts.map((c) => c.qtde),
                        backgroundColor: counts.map((c) => PAYMENT_METHOD_COLOR[c.key] || '#9AA0A6'),
                        borderRadius: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateSubscriptionStatus(purchases: Purchase[]) {
        let counts: { key: string; qtde: number }[] = []
        for (const purchase of purchases) {
            const key = purchase.statusLabel
            let item = counts.find((el) => el.key === key)
            if (!item) {
                item = { key, qtde: 0 }
                counts.push(item)
            }
            item.qtde += 1
        }
        counts.sort((a, b) => b.qtde - a.qtde)

        const chart = new Chart(containerAssinaturas, {
            type: 'polarArea',
            data: {
                labels: counts.map((c) => PurchaseStatusFriendly[c.key] || c.key),
                datasets: [{ data: counts.map((c) => c.qtde), backgroundColor: counts.map((c) => hexToRgba(PURCHASE_STATUS_COLOR[c.key] || '#9AA0A6', 0.65)) }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.label + ': ' + ctx.formattedValue } },
                },
                scales: {
                    r: { grid: { color: GRID }, angleLines: { color: GRID }, ticks: { display: false, backdropColor: 'transparent' } },
                },
            },
        })
        charts.push(chart)
    }

    async function generateOverdueHistogram(purchases: Purchase[]) {
        const filtered = purchases.filter((el) => el.status === PurchaseStatus.active || el.status === PurchaseStatus.overdue)
        const STEP_DAYS = 10
        const MAX_DAYS = 90
        const COLORS = ['#8BC34A', '#FEB019', '#FFA000', '#FF8C42', '#FF6F3C', '#D0483E', '#C0392B', '#A93226', '#8B1E1E']
        let buckets: { label: string; min: number; max: number; color: string }[] = [{ label: 'Em dia', min: -Infinity, max: 0, color: '#379E75' }]
        for (let d = 1; d <= MAX_DAYS; d += STEP_DAYS) {
            buckets.push({
                label: d + ' a ' + (d + STEP_DAYS - 1) + ' dias',
                min: d,
                max: d + STEP_DAYS - 1,
                color: COLORS[(buckets.length - 1) % COLORS.length],
            })
        }
        buckets.push({ label: 'Mais de ' + MAX_DAYS + ' dias', min: MAX_DAYS + 1, max: Infinity, color: '#6B1414' })
        const data = buckets.map((b) => filtered.filter((p) => (p.pastDueDays || 0) >= b.min && (p.pastDueDays || 0) <= b.max).length)

        const chart = new Chart(containerAtraso, {
            type: 'bar',
            data: {
                labels: buckets.map((b) => b.label),
                datasets: [{ label: 'Assinaturas', data, backgroundColor: buckets.map((b) => b.color), borderRadius: 4 }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    // por meio de pagamento (purchaseMethod — o meio atual; quem trocou de meio conta no novo):
    // inadimplência = em atraso / (ativas + em atraso), ou seja, sobre quem ainda está sendo cobrado;
    // cancelamento = canceladas / (ativas + em atraso + canceladas), ou seja, sobre quem chegou a ser ativado.
    // pendentes ficam fora das duas contas: ainda não começaram a ser cobradas.
    async function generateInadimplenciaCancelamento(purchases: Purchase[]) {
        const ORDER = ['credit', 'pix', 'bankslip']
        let rows: { key: string; ativas: number; atraso: number; canceladas: number }[] = []
        for (const p of purchases) {
            const key = ORDER.includes(p.purchaseMethod) ? p.purchaseMethod : 'other'
            let row = rows.find((r) => r.key === key)
            if (!row) {
                row = { key, ativas: 0, atraso: 0, canceladas: 0 }
                rows.push(row)
            }
            if (p.status === PurchaseStatus.active) row.ativas += 1
            if (p.status === PurchaseStatus.overdue) row.atraso += 1
            if (p.status === PurchaseStatus.canceled) row.canceladas += 1
        }
        rows = rows
            .filter((r) => r.ativas + r.atraso + r.canceladas > 0)
            .sort((a, b) => (ORDER.indexOf(a.key) === -1 ? 99 : ORDER.indexOf(a.key)) - (ORDER.indexOf(b.key) === -1 ? 99 : ORDER.indexOf(b.key)))

        const percent = (part: number, whole: number) => (whole > 0 ? Number(((100 / whole) * part).toFixed(1)) : 0)
        const inad = rows.map((r) => ({ value: percent(r.atraso, r.ativas + r.atraso), part: r.atraso, whole: r.ativas + r.atraso }))
        const canc = rows.map((r) => ({
            value: percent(r.canceladas, r.ativas + r.atraso + r.canceladas),
            part: r.canceladas,
            whole: r.ativas + r.atraso + r.canceladas,
        }))

        const chart = new Chart(containerInadCanc, {
            type: 'bar',
            data: {
                labels: rows.map((r) => PAYMENT_METHOD_LABEL[r.key] || 'Outro'),
                datasets: [
                    { label: 'Inadimplência', data: inad.map((d) => d.value), backgroundColor: '#D0483E', borderRadius: 4 },
                    { label: 'Cancelamento', data: canc.map((d) => d.value), backgroundColor: '#9AA0A6', borderRadius: 4 },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço acima da barra mais alta para o datalabel não ficar cortado
                layout: { padding: { top: 24 } },
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const d = (ctx.datasetIndex === 0 ? inad : canc)[ctx.dataIndex]
                                return ctx.dataset.label + ': ' + d.value.toFixed(1) + '% (' + d.part + ' de ' + d.whole + ')'
                            },
                        },
                    },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => val.toFixed(1) + '%' },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { beginAtZero: true, grid: { display: false }, ticks: { display: false } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    // novas assinaturas (por criação) x canceladas (proxy: updatedAt de assinaturas com status cancelado), mês a mês, últimos 24 meses
    async function generateCrescimento(purchases: Purchase[]) {
        const filtered = purchases.filter(
            (el) => el.status === PurchaseStatus.active || el.status === PurchaseStatus.canceled || el.status === PurchaseStatus.overdue,
        )
        const buckets = buildMonthBuckets(24)
        const novas = buckets.map((b) => filtered.filter((p) => new Date(p.createdAt) >= b.from && new Date(p.createdAt) <= b.to).length)
        const canceladas = buckets.map(
            (b) => filtered.filter((p) => p.status === PurchaseStatus.canceled && new Date(p.updatedAt) >= b.from && new Date(p.updatedAt) <= b.to).length,
        )

        const chart = new Chart(containerCrescimento, {
            type: 'bar',
            data: {
                labels: buckets.map((b) => b.label),
                datasets: [
                    { label: 'Novas assinaturas', data: novas, backgroundColor: '#379E75', borderRadius: 4 },
                    { label: 'Canceladas', data: canceladas, backgroundColor: '#D0483E', borderRadius: 4 },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { mode: 'index', intersect: false },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
        })
        charts.push(chart)
    }

    async function generateTipoDistribuicao(purchases: Purchase[], canvas: HTMLCanvasElement) {
        let counts: { key: string; qtde: number }[] = []
        for (const p of purchases) {
            const key = p.purchaseType
            let item = counts.find((el) => el.key === key)
            if (!item) {
                item = { key, qtde: 0 }
                counts.push(item)
            }
            item.qtde += 1
        }
        counts.sort((a, b) => b.qtde - a.qtde)

        const chart = new Chart(canvas, {
            type: 'doughnut',
            data: {
                labels: counts.map((c) => PURCHASE_TYPE_LABEL[c.key] || c.key),
                datasets: [
                    {
                        data: counts.map((c) => c.qtde),
                        backgroundColor: counts.map((c) => PURCHASE_TYPE_COLOR[c.key] || '#9AA0A6'),
                        borderColor: 'transparent',
                        borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.label + ': ' + ctx.parsed } },
                    datalabels: { color: '#fff', font: { size: 11, weight: 'bold' }, formatter: (val) => (val > 0 ? val : '') },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateTicketPorTipo(purchases: Purchase[], canvas: HTMLCanvasElement) {
        let groups: { key: string; total: number; qtde: number }[] = []
        for (const p of purchases) {
            const key = p.purchaseType
            let item = groups.find((el) => el.key === key)
            if (!item) {
                item = { key, total: 0, qtde: 0 }
                groups.push(item)
            }
            item.total += p.purchaseTotalValue || 0
            item.qtde += 1
        }
        groups.sort((a, b) => b.total / b.qtde - a.total / a.qtde)

        const chart = new Chart(canvas, {
            type: 'bar',
            data: {
                labels: groups.map((g) => PURCHASE_TYPE_LABEL[g.key] || g.key),
                datasets: [
                    {
                        label: 'Ticket médio',
                        data: groups.map((g) => Number((g.total / g.qtde).toFixed(2))),
                        backgroundColor: groups.map((g) => PURCHASE_TYPE_COLOR[g.key] || '#9AA0A6'),
                        borderRadius: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => currency(ctx.parsed.y) } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => currency(val) },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    // tempo entre criação e cancelamento (proxy via updatedAt) das assinaturas canceladas, em faixas de 3 em 3 meses até 30 meses
    async function generateTempoCancelamento(purchases: Purchase[]) {
        const canceladas = purchases.filter((p) => p.status === PurchaseStatus.canceled)
        const STEP_MONTHS = 3
        const MAX_MONTHS = 30
        // cancelamento cedo (ruim) em vermelho — só as 2 primeiras faixas — depois transiciona de amarelo para verde
        const COLORS = ['#8B1E1E', '#D0483E', '#FFA000', '#FFB300', '#F9C74F', '#D4D94A', '#A9D18E', '#8BC34A', '#6FBF73', '#4CAF7D', '#379E75']
        let buckets: { label: string; min: number; max: number; color: string }[] = []
        for (let m = 0; m < MAX_MONTHS; m += STEP_MONTHS) {
            buckets.push({
                label: m + ' a ' + (m + STEP_MONTHS) + ' meses',
                min: m * 30,
                max: (m + STEP_MONTHS) * 30 - 1,
                color: COLORS[buckets.length],
            })
        }
        buckets.push({ label: 'Mais de ' + MAX_MONTHS + ' meses', min: MAX_MONTHS * 30, max: Infinity, color: COLORS[buckets.length] })
        const counts = buckets.map(
            (b) =>
                canceladas.filter((p) => {
                    const dias = dayjs(p.updatedAt).diff(dayjs(p.createdAt), 'day')
                    return dias >= b.min && dias <= b.max
                }).length,
        )
        const total = canceladas.length
        const data = counts.map((c) => (total > 0 ? Number(((100 / total) * c).toFixed(1)) : 0))

        const chart = new Chart(containerTempoCancelamento, {
            type: 'bar',
            data: {
                labels: buckets.map((b) => b.label),
                datasets: [{ label: '% dos cancelamentos', data, backgroundColor: buckets.map((b) => b.color), borderRadius: 4 }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => ctx.parsed.y.toFixed(1) + '% (' + counts[ctx.dataIndex] + ' de ' + total + ')' } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => (val > 0 ? val.toFixed(1) + '%' : '') },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    function evaluateError(error) {
        window.error(error)
    }

    onMount(async () => {
        await getData()
    })
</script>

<Zframe title="Indicadores">
    <div class="base-dash sales-dash">
        <Zact>
            <div class="safra-filter">
                <Zinput type="select" label="Safra" options={safraOptions} bind:value={safra} on:change={renderIndicadores} />
            </div>
        </Zact>

        <ZContainer class="h-space">
            <Zframe
                size={6}
                sizeM={12}
                title="Cobrança - Assinaturas por status"
                headline="Quantidade de assinaturas por status atual (ativa, pendente, em atraso ou cancelada), entre as assinaturas da safra selecionada — a safra é definida pela data de criação da assinatura. Apenas assinaturas recorrentes (prazo fixo ou contínua)."
            >
                <div class="chart-box">
                    <canvas bind:this={containerAssinaturas}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Cobrança - Inadimplência e cancelamento por meio de pagamento"
                headline="Para cada meio de pagamento (cartão, pix ou boleto), considerando as assinaturas recorrentes (prazo fixo ou contínua) da safra selecionada. Inadimplência: percentual das assinaturas em cobrança (ativas ou em atraso) que estão em atraso hoje. Cancelamento: percentual das assinaturas que já foram ativadas (ativas, em atraso ou canceladas) e foram canceladas. Assinaturas pendentes ficam de fora. Vale o meio de pagamento atual de cada assinatura."
            >
                <div class="chart-box">
                    <canvas bind:this={containerInadCanc}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={12}
                sizeM={12}
                title="Cobrança - Assinaturas por tempo de atraso hoje"
                headline="Quantidade de assinaturas agrupadas pelo tempo de atraso atual (faixas de 10 em 10 dias até 90 dias, com uma faixa final para mais de 90 dias), entre as assinaturas da safra selecionada. Apenas assinaturas recorrentes (prazo fixo ou contínua)."
            >
                <div class="chart-box">
                    <canvas bind:this={containerAtraso}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={12}
                title="Cobrança - Tempo até o cancelamento"
                headline="Das assinaturas recorrentes da safra selecionada que já foram canceladas, qual percentual cancelou em cada faixa de tempo desde a criação (aproximado pela data de última atualização), em faixas de 3 em 3 meses até 30 meses. Cobranças únicas (não recorrentes) são ignoradas. É a distribuição dos cancelamentos entre si — não é a mesma conta que a taxa de churn mensal (que compara com a base ativa em cada mês, não com o total já cancelado). Atenção: em safras recentes os cancelamentos se concentram nas primeiras faixas porque as assinaturas ainda não tiveram tempo de cancelar mais tarde — é efeito da idade da safra, não necessariamente de clientes saindo cedo."
            >
                <div class="chart-box">
                    <canvas bind:this={containerTempoCancelamento}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Cobrança - Distribuição por tipo de assinatura"
                headline="Quantidade de assinaturas por tipo — prazo fixo, contínua ou cobrança única — entre as assinaturas da safra selecionada."
            >
                <div class="chart-box chart-box_fill">
                    <canvas bind:this={containerTipo}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Cobrança - Ticket médio por tipo de assinatura"
                headline="Valor médio contratado para cada tipo de assinatura, entre as assinaturas da safra selecionada."
            >
                <div class="chart-box chart-box_fill">
                    <canvas bind:this={containerTicketTipo}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={12}
                title="Cobrança - Crescimento líquido de assinaturas"
                headline="Novas assinaturas (por data de criação) x canceladas (por data da última atualização, usada como aproximação da data de cancelamento), mês a mês, últimos 24 meses — considerando toda a base, independente da safra selecionada. Apenas assinaturas recorrentes (prazo fixo ou contínua)."
            >
                <div class="chart-box">
                    <canvas bind:this={containerCrescimento}></canvas>
                </div>
            </Zframe>
        </ZContainer>
    </div>
</Zframe>

<Zframe title="Por período" class="h-space">
    <div class="base-dash sales-dash">
        <Zact>
            <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        </Zact>

        <ZContainer class="h-space">
            <Zframe
                size={12}
                title="Parcela - Cobrado x recebido"
                headline="Compara o valor cobrado (por data de vencimento) com o valor efetivamente recebido (por data de pagamento) no período. A diferença entre as linhas indica o quanto ainda está em aberto ou foi perdido. Parcelas negadas ou anuladas são ignoradas."
            >
                <div class="chart-box">
                    <canvas bind:this={containerEvolucao}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Parcela - Cobranças por status"
                headline="Distribuição das parcelas emitidas no período por status (pago, pendente, estornado, etc). Parcelas negadas ou anuladas são ignoradas."
            >
                <div class="chart-box chart-box_fill">
                    <canvas bind:this={containerStatus}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Parcela - Taxa de aprovação"
                headline="Percentual de parcelas pagas em relação ao total de parcelas já concluídas (pagas + falhas/canceladas/estornadas). Parcelas ainda pendentes, negadas ou anuladas não entram nesse cálculo."
            >
                <div class="chart-box chart-box_fill">
                    <canvas bind:this={containerAprovacao}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Parcela - Meio de pagamento das cobranças"
                headline="Quantidade de parcelas emitidas no período por meio de pagamento (cartão, pix ou boleto). Parcelas negadas ou anuladas são ignoradas."
            >
                <div class="chart-box">
                    <canvas bind:this={containerMetodo}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Cobrança - Ticket médio por tipo de assinatura"
                headline="Valor médio contratado para cada tipo de assinatura, entre as assinaturas criadas no período."
            >
                <div class="chart-box chart-box_fill">
                    <canvas bind:this={containerTicketTipoPeriodo}></canvas>
                </div>
            </Zframe>
        </ZContainer>
    </div>
</Zframe>
<LoadSpinner delay={300} {show} />

<style>
    .chart-box {
        position: relative;
        width: 100%;
        height: 320px;
    }
    .chart-box_fill {
        height: 100%;
        min-height: 320px;
    }
    .safra-filter {
        min-width: 240px;
    }
</style>
